<script setup lang="ts">
  import { computed, inject, ref, useTemplateRef } from 'vue';

  import { t } from '../../locale';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import { DATA_VIEW_INJECTION } from '../DataView/DataView.keys';
  import { TABLE_INJECTION } from '../Table/Table.vue';
  import TableHeaderCell from '../TableHeaderCell/TableHeaderCell.vue';

  export interface TableHeaderRowProps {
    allRowsSelected?: boolean;
    someRowsSelected?: boolean;
  }

  const props = withDefaults(defineProps<TableHeaderRowProps>(), {
    allRowsSelected: false,
    someRowsSelected: false,
  });

  const emit = defineEmits<{
    (e: 'select'): void;
  }>();

  const tableInjection = inject(TABLE_INJECTION.key);

  if (!tableInjection) {
    throw new Error('TableHeaderRow must be used within a Table component');
  }

  const { hasCustomExpandToggle, isSchemaDriven, isExpandable, isSelectable } = tableInjection;

  const showSelectionCells = computed(() => isSelectable?.value && !isSchemaDriven?.value);

  const { hasToolbar } = inject(DATA_VIEW_INJECTION.key, DATA_VIEW_INJECTION.defaults);

  const checkboxKey = ref(0);

  const rowElement = useTemplateRef('rowElement');

  defineExpose({
    rowElement,
  });

  function onSelect() {
    emit('select');

    /**
     * In Vue (as of this writing), if a user checks a native checkbox but the v-model is `false` and does not change from `false`, then the checkbox will display as checked even though the v-model is still `false`.
     * Forcing a re-render with a `key` change allows the checkbox to stay unchecked if its v-model is still `false` and its v-model not been changed.
     * To verify that this is necessary, test the "select all" checkbox in the SelectionWithSomeDisabled story in Table.story.ts with and without the `key` attribute.
     */
    checkboxKey.value++;
  }
</script>

<template>
  <tr ref="rowElement" class="stash-table-header-row" data-test="stash-table-header-row">
    <!-- This is just an empty cell to ensure proper alignment. "Select all" control should be in the toolbar. -->
    <TableHeaderCell
      v-if="hasToolbar && showSelectionCells"
      class="stash-table-header-row__selection-cell-empty"
      :class="$style['row-control-cell']"
      data-test="table-row-header|selection-cell-empty"
    />

    <TableHeaderCell
      v-if="!hasToolbar && showSelectionCells"
      class="stash-table-header-row__selection-cell min-w-12"
      :class="$style['row-control-cell']"
      data-test="table-row-header|selection-cell"
    >
      <Checkbox
        v-if="!hasToolbar"
        :key="checkboxKey"
        :class="$style['row-selection-checkbox']"
        :checked="props.allRowsSelected"
        :indeterminate="props.someRowsSelected && !props.allRowsSelected"
        :title="t('ll.selectAll')"
        @update:indeterminate="onSelect"
        @update:checked="onSelect"
      />
    </TableHeaderCell>

    <TableHeaderCell
      v-if="isExpandable && !hasCustomExpandToggle"
      :class="$style['row-control-cell']"
      data-test="table-row-header-expansion-cell"
    />
    <!-- @slot default -->
    <slot></slot>
  </tr>
</template>

<style module>
  @reference "../../../styles/main.css";

  .row-selection-checkbox label {
    padding: 0;
    min-height: --spacing(6);
    vertical-align: middle;
  }

  .row-control-cell {
    border-right: 0;
  }
</style>
